import React, { useState } from 'react';

import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { findLastIndex } from 'lodash-es';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { MainContentCmp, AgentVegaChartAsync } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp';
import AgentThinkTool from '@/pages/aigc/assets/svg/agent-think-tool-use.svg';
import {
  ISwitchAgentAccordionGroup,
  ISwitchAgentAccordionItem,
} from '@/pages/aigc/components/SwitchAgentAccordion';

interface IToolUseThinkCollapseProps {
  title: React.ReactNode;
  thinkValue?: string;
  defaultOpen?: boolean;
}

const ToolUseThinkCollapse: React.FC<IToolUseThinkCollapseProps> = ({
  title,
  thinkValue,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const hasContent = !!thinkValue?.trim();

  const handleToggle = (): void => {
    if (!hasContent) return;
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="dba-tool-use-collapse-container">
      <div
        className={classNames('dba-tool-use-collapse-header', {
          'dba-tool-use-collapse-header--disabled': !hasContent,
        })}
        onClick={handleToggle}
      >
        <span className="dba-tool-use-collapse-title">{title}</span>
        {hasContent && <SvgIcon type={isOpen ? 'point-top' : 'point-down'} />}
      </div>
      {hasContent && (
        <Collapse isOpen={isOpen}>
          <div className="dba-tool-use-collapse-content">
            <MainContentCmp
              value={thinkValue || ''}
              className="dba-tool-use-collapse-markdown"
              isShowReference={false}
            />
          </div>
        </Collapse>
      )}
    </div>
  );
};

const getToolUseKey = (thinkId?: string, toolCallId?: string): string =>
  `${thinkId || ''}__${toolCallId || ''}`;

interface IBuildAgentFoldAccordionParams {
  accordionSource: ISwitchAgentAccordionItem[];
  formatMessage: (descriptor: { id: string; }) => string;
  streamingState?: string;
  messageId?: string;
}

/** think_fold_mode=agent 时使用，下线 agent 后删除本文件 */
export const buildAgentFoldAccordion = ({
  accordionSource,
  formatMessage,
  streamingState,
  messageId,
}: IBuildAgentFoldAccordionParams): ISwitchAgentAccordionGroup[] => {
  if (!accordionSource?.length) return [];

  const toolUseKeySet = new Set<string>();
  accordionSource.forEach((item) => {
    if (item?.type === 'tool_use' && item?.thinkId && item?.tool_call_id) {
      toolUseKeySet.add(getToolUseKey(String(item.thinkId), String(item.tool_call_id)));
    }
  });

  const matchedThinkToolIndexSet = new Set<number>();
  const toolUseThinkValueMap = new Map<string, string[]>();
  accordionSource.forEach((item, index) => {
    if (item?.type !== 'think_tool' || !item?.thinkId || !item?.tool_call_id) {
      return;
    }
    const key = getToolUseKey(String(item.thinkId), String(item.tool_call_id));
    if (!toolUseKeySet.has(key)) {
      return;
    }
    const currentList = toolUseThinkValueMap.get(key) || [];
    toolUseThinkValueMap.set(key, [...currentList, String(item?.value || '')]);
    matchedThinkToolIndexSet.add(index);
  });

  const toolUseThinkMap = new Map<string, string>();
  toolUseThinkValueMap.forEach((list, key) => {
    toolUseThinkMap.set(key, list.filter(Boolean).join('\n'));
  });

  const isMetadataText = (item: ISwitchAgentAccordionItem): boolean =>
    item.type === 'text_tool' || (item.type === 'text' && !!item.tool_call_id);

  const enhanceItem = (
    item: ISwitchAgentAccordionItem,
    idx: number,
    arrList?: ISwitchAgentAccordionItem[],
  ): ISwitchAgentAccordionItem => {
    if (item.type === 'switch_agent') return item;

    if (item.type === 'think' || item.type === 'think_tool' || item.type === 'text_tool' || isMetadataText(item)) {
      return {
        ...item,
        content: (
          <div
            key={`${item.thinkId || idx}-${item.type}`}
            className={classNames({
              'dba-text-tool-item': item.type === 'text_tool' || isMetadataText(item),
            })}
          >
            <MainContentCmp
              value={item.value}
              streamingState={streamingState}
              from="think"
              messageId={messageId}
              className={
                item.type === 'text_tool' || isMetadataText(item)
                  ? 'dba-text-tool-markdown'
                  : undefined
              }
            />
          </div>
        ),
      };
    }

    if (item.type === 'bq_chart') {
      return {
        ...item,
        content: (
          <AgentVegaChartAsync
            key={`${item.thinkId || idx}-bq_chart`}
            value={item.value}
            messageId={messageId}
            from="think"
          />
        ),
      };
    }

    if (!item.input && item.type !== 'think') {
      const messageMap = {
        switch_agent: () =>
          `${formatMessage({ id: 'dba_switch_agent' })}${item.new_agent}`,
        tool_use: () => (
          <>
            <img
              src={AgentThinkTool}
              alt=""
              className="dba-tool-use-icon"
            />
            {`${formatMessage({ id: 'dba_switch_tool' })}${item.value}`}
          </>
        ),
        error: () => formatMessage({ id: 'dba_chart_stop' }),
        default: () => item.value || '-',
      };
      const getMessage =
        messageMap[item.type as keyof typeof messageMap] ||
        messageMap.default;
      const message = getMessage();
      const thinkId = item?.thinkId;
      let isToolMarginBt = false;
      if (item?.type === 'tool_use') {
        const lastToolUseIndex = findLastIndex(arrList, {
          type: 'tool_use',
          thinkId,
        });
        const isShowLastToolUse = findLastIndex(arrList, { thinkId });
        if (
          isShowLastToolUse === lastToolUseIndex &&
          lastToolUseIndex === idx
        ) {
          isToolMarginBt = true;
        }
      }
      const contentNode = (
        <span
          key={`${thinkId || idx}-text`}
          className={classNames('dba-system-message-input', {
            'dba-system-message-input-tool-use': isToolMarginBt,
          })}
        >
          {message}
        </span>
      );

      const toolThinkValue = item?.type === 'tool_use' && item?.thinkId && item?.tool_call_id
        ? toolUseThinkMap.get(getToolUseKey(String(item.thinkId), String(item.tool_call_id))) || ''
        : '';

      return {
        ...item,
        content: (
          <>
            {item?.type === 'tool_use' ? (
              <ToolUseThinkCollapse
                title={contentNode}
                thinkValue={toolThinkValue}
                defaultOpen={false}
              />
            ) : (
              contentNode
            )}
          </>
        ),
      };
    }

    return item;
  };

  const result: ISwitchAgentAccordionGroup[] = [];
  let currentGroup: ISwitchAgentAccordionGroup | null = null;

  accordionSource.forEach((item, idx, arrList) => {
    if (!item?.thinkId) return;

    if (item.type === 'think_tool' && matchedThinkToolIndexSet.has(idx)) {
      return;
    }

    const enhancedItem = enhanceItem(item, idx, arrList);

    if (enhancedItem.type === 'switch_agent') {
      currentGroup = {
        id: String(item.thinkId),
        header: enhancedItem,
        items: [],
      };
      result.push(currentGroup);
      return;
    }

    if (currentGroup && currentGroup.id === item.thinkId) {
      currentGroup.items.push(enhancedItem);
    }
  });

  return result;
};
